import { DashboardOutlined } from '@ant-design/icons';
import { ErrorComponent, ThemedLayout, useNotificationProvider } from '@refinedev/antd';
import { Authenticated, Refine } from '@refinedev/core';
import routerProvider, { CatchAllNavigate, DocumentTitleHandler, NavigateToResource } from '@refinedev/react-router';
import { App as AntdApp, ConfigProvider, theme } from 'antd';
import { useEffect, useState } from 'react';
import { BrowserRouter, Outlet, Route, Routes } from 'react-router';
import { Dashboard } from './pages/dashboard';
import { Login } from './pages/login';
import { ResourceList } from './pages/resource-list';
import { ResourceShow } from './pages/resource-show';
import { authProvider, dataProvider } from './providers';
import { GROUPS, RESOURCES } from './resources';

/** Follows the system's light or dark mode. */
function usePrefersDark(): boolean {
	const query = typeof window === 'undefined' ? null : window.matchMedia( '(prefers-color-scheme: dark)' );
	const [ dark, setDark ] = useState( query?.matches ?? false );
	useEffect( () => {
		const listener = ( event: MediaQueryListEvent ) => setDark( event.matches );
		query?.addEventListener( 'change', listener );
		return () => query?.removeEventListener( 'change', listener );
	}, [ query ] );
	return dark;
}

const refineResources = [
	{ name: 'overview', list: '/', meta: { label: 'Overview', icon: <DashboardOutlined /> } },
	...Object.entries( GROUPS ).map( ( [ name, label ] ) => ( { name, meta: { label } } ) ),
	...RESOURCES.map( ( r ) => ( { name: r.name, list: `/${ r.name }`, show: `/${ r.name }/show/:id`, meta: { label: r.label, parent: r.group } } ) ),
];

export function App() {
	const dark = usePrefersDark();
	return (
		<BrowserRouter>
			<ConfigProvider theme={ { algorithm: dark ? theme.darkAlgorithm : theme.defaultAlgorithm } }>
				<AntdApp>
					<Refine
						dataProvider={ dataProvider }
						authProvider={ authProvider }
						routerProvider={ routerProvider }
						notificationProvider={ useNotificationProvider }
						resources={ refineResources }
						options={ { syncWithLocation: true, disableTelemetry: true, title: { text: 'C1 Sports' } } }
					>
						<Routes>
							<Route
								element={
									<Authenticated key="admin" fallback={ <CatchAllNavigate to="/login" /> }>
										<ThemedLayout>
											<Outlet />
										</ThemedLayout>
									</Authenticated>
								}
							>
								<Route index element={ <Dashboard /> } />
								{ RESOURCES.map( ( r ) => (
									<Route key={ r.name } path={ `/${ r.name }` }>
										<Route index element={ <ResourceList config={ r } /> } />
										<Route path="show/:id" element={ <ResourceShow config={ r } /> } />
									</Route>
								) ) }
								<Route path="*" element={ <ErrorComponent /> } />
							</Route>
							<Route
								path="/login"
								element={
									<Authenticated key="login" fallback={ <Login /> }>
										<NavigateToResource resource="overview" />
									</Authenticated>
								}
							/>
						</Routes>
						<DocumentTitleHandler />
					</Refine>
				</AntdApp>
			</ConfigProvider>
		</BrowserRouter>
	);
}
